import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Trophy,
  CheckCircle,
  XCircle,
  ArrowRight,
  RotateCcw,
  Home,
  Clock
} from "lucide-react";

const QuizResult = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { result, category } = location.state || {};

  if (!result) {
    navigate("/dashboard");
    return null;
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getScoreMessage = () => {
    if (result.percentage >= 80) return { text: "Excellent !", color: "text-green-600" };
    if (result.percentage >= 60) return { text: "Bien joué !", color: "text-blue-600" };
    if (result.percentage >= 40) return { text: "Peut mieux faire", color: "text-amber-600" };
    return { text: "Continuez à réviser", color: "text-red-600" };
  };

  const scoreMessage = getScoreMessage();

  return (
    <div className="min-h-screen bg-slate-50" data-testid="quiz-result-page">
      <Navbar />
      
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Score Summary */}
        <Card className="mb-8 overflow-hidden">
          <div className="hero-gradient text-white p-8 text-center">
            <div className="h-20 w-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trophy className="h-10 w-10" />
            </div>
            <h1 className="text-4xl font-serif font-bold mb-2">
              {result.score}/{result.total}
            </h1>
            <p className="text-blue-100 text-lg">{result.percentage}% de bonnes réponses</p>
          </div>
          <CardContent className="p-6">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className={`text-2xl font-bold ${scoreMessage.color}`}>{scoreMessage.text}</p>
                <p className="text-sm text-slate-500">Performance</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{result.score}</p>
                <p className="text-sm text-slate-500">Bonnes réponses</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-1 text-2xl font-bold text-slate-900">
                  <Clock className="h-5 w-5" />
                  {formatTime(result.time_spent)}
                </div>
                <p className="text-sm text-slate-500">Temps</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Detailed Results */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="font-serif">Détail des réponses</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {result.results?.map((item, index) => (
              <div 
                key={index}
                className={`p-4 rounded-lg ${item.is_correct ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  {item.is_correct ? (
                    <CheckCircle className="h-6 w-6 text-green-600 shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="h-6 w-6 text-red-600 shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className="font-medium text-slate-900 mb-2">
                      {index + 1}. {item.question}
                    </p>
                    <div className="space-y-1 text-sm">
                      <p className={item.is_correct ? 'text-green-700' : 'text-red-700'}>
                        Votre réponse : {item.options[item.user_answer]}
                      </p>
                      {!item.is_correct && (
                        <p className="text-green-700">
                          Bonne réponse : {item.options[item.correct_answer]}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="ml-9 p-3 bg-white/50 rounded-lg">
                  <p className="text-sm text-slate-600">
                    <span className="font-medium">Explication :</span> {item.explanation}
                  </p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => navigate("/dashboard")}
            data-testid="back-to-dashboard-btn"
          >
            <Home className="h-4 w-4 mr-2" />
            Tableau de bord
          </Button>
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => navigate(`/quiz/${category}`)}
            data-testid="retry-quiz-btn"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Réessayer
          </Button>
          <Button
            className="flex-1 bg-blue-900 hover:bg-blue-800"
            onClick={() => navigate("/fiches")}
            data-testid="go-to-fiches-btn"
          >
            Réviser les fiches
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </main>
    </div>
  );
};

export default QuizResult;
